class PRO2OProductFeatures {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.tabsNav = this.section.querySelector('[data-tabs-nav]');
    this.imageContainer = this.section.querySelector('[data-image-container]');
    this.tabImages = new Map();
    this.tabButtons = [];

    // Get the data attribute from the pvl-features-tabs element
    const tabsContainer = this.section.querySelector('[data-features-tabs]');
    this.enableTransition = tabsContainer?.dataset.enableImageTransition === 'true';

    this.init();
  }

  init() {
    if (!this.section) {
      console.warn('PROH2O Features: Section element not found');
      return;
    }

    // Build tabs from data elements
    this.buildTabsFromData();

    // Only proceed if tabs were created
    if (this.tabButtons.length === 0) {
      console.warn('PROH2O Features: No tab data found');
      return;
    }

    this.buildTabImageMap();
    this.bindEvents();
    this.setupKeyboardNavigation();

    // Display the first tab's image
    if (this.tabButtons.length > 0) {
      this.displayImage(this.tabButtons[0]);
    }

    console.log(`PROH2O Features initialized with ${this.tabButtons.length} tabs`);
    console.log(`Image transition animation: ${this.enableTransition ? 'enabled' : 'disabled'}`);
  }

  buildTabsFromData() {
    // Find all feature tab data elements
    const tabDataElements = this.section.querySelectorAll('.feature-tab-data');

    if (!tabDataElements.length || !this.tabsNav) {
      return;
    }

    // Create tab buttons from data
    tabDataElements.forEach((dataEl, index) => {
      const tabId = dataEl.dataset.tabId;
      const title = dataEl.dataset.tabTitle || `Feature ${index + 1}`;
      const description = dataEl.dataset.tabDescription || '';
      const imageUrl = dataEl.dataset.tabImage || '';
      const imageAlt = dataEl.dataset.tabImageAlt || 'Feature image';
      const imageWidth = dataEl.dataset.tabImageWidth || '600';
      const imageHeight = dataEl.dataset.tabImageHeight || '500';
      const imageRadius = dataEl.dataset.tabImageRadius || '8';

      // Create button element
      const button = document.createElement('button');
      button.className = `tab-button${index === 0 ? ' active' : ''}`;
      button.setAttribute('data-tab', tabId);
      button.setAttribute('data-image-url', imageUrl);
      button.setAttribute('data-image-alt', imageAlt);
      button.setAttribute('data-image-width', imageWidth);
      button.setAttribute('data-image-height', imageHeight);
      button.setAttribute('data-image-radius', imageRadius);
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');

      // Add title
      const titleEl = document.createElement('h3');
      titleEl.className = 'tab-title';
      titleEl.textContent = title;

      // Add description (supports richtext HTML)
      // Wrap richtext in an inner div so the description can animate via
      // grid-template-rows: 0fr → 1fr (needs exactly one collapsible child).
      const descEl = document.createElement('div');
      descEl.className = 'tab-description';
      const descInner = document.createElement('div');
      descInner.className = 'tab-description-inner';
      descInner.innerHTML = description;
      descEl.appendChild(descInner);

      button.appendChild(titleEl);
      button.appendChild(descEl);

      this.tabsNav.appendChild(button);
      this.tabButtons.push(button);
    });
  }

  buildTabImageMap() {
    // Build map from data attributes
    this.tabButtons.forEach(button => {
      const tabId = button.getAttribute('data-tab');
      const imageUrl = button.getAttribute('data-image-url');
      const imageAlt = button.getAttribute('data-image-alt');
      const imageWidth = button.getAttribute('data-image-width');
      const imageHeight = button.getAttribute('data-image-height');
      const imageRadius = button.getAttribute('data-image-radius');

      if (tabId) {
        this.tabImages.set(tabId, {
          url: imageUrl || null,
          alt: imageAlt || 'Feature showcase',
          width: imageWidth || '600',
          height: imageHeight || '500',
          radius: imageRadius || '8'
        });
      }
    });
  }

  displayImage(button) {
    if (!this.imageContainer) return;

    const tabId = button.getAttribute('data-tab');
    const imageData = this.tabImages.get(tabId);

    if (!imageData?.url) {
      this.imageContainer.innerHTML = '<div class="placeholder-image"><span>No image available</span></div>';
      return;
    }

    this.imageContainer.innerHTML = `
      <img
        src="${imageData.url}"
        alt="${imageData.alt}"
        width="${imageData.width}"
        height="${imageData.height}"
        class="feature-image"
        style="border-radius: ${imageData.radius}px;"
        loading="lazy"
      >
    `;
  }

  bindEvents() {
    this.tabButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleTabClick(e, button));
    });
  }

  handleTabClick(event, button) {
    event.preventDefault();

    // Update active states
    this.updateActiveStates(button);

    // Handle image transition (with or without animation)
    if (this.enableTransition) {
      this.animateImageTransition(button);
    } else {
      this.displayImage(button);
    }

    // Dispatch custom event for external listeners
    this.section.dispatchEvent(new CustomEvent('tabchange', {
      detail: {
        activeTab: button.getAttribute('data-tab'),
        tabTitle: button.querySelector('.tab-title')?.textContent
      }
    }));
  }

  updateActiveStates(activeButton) {
    this.tabButtons.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
    });

    activeButton.classList.add('active');
    activeButton.setAttribute('aria-selected', 'true');
  }

  animateImageTransition(activeButton) {
    if (!this.imageContainer) return;

    const tabId = activeButton.getAttribute('data-tab');
    const imageData = this.tabImages.get(tabId);

    if (!imageData?.url) {
      this.displayImage(activeButton);
      return;
    }

    // Start transition animation
    this.imageContainer.style.willChange = 'transform, opacity';
    this.imageContainer.style.opacity = '0.3';
    this.imageContainer.style.transform = 'translateX(-30px) translateY(20px)';

    // Update image after fade out
    setTimeout(() => {
      this.displayImage(activeButton);

      // Fade in animation
      setTimeout(() => {
        this.imageContainer.style.opacity = '1';
        this.imageContainer.style.transform = 'translateX(0px) translateY(0px)';

        // Reset performance optimization
        setTimeout(() => {
          this.imageContainer.style.willChange = 'auto';
        }, 600);
      }, 50);
    }, 300);
  }

  setupKeyboardNavigation() {
    this.tabButtons.forEach((button, index) => {
      button.addEventListener('keydown', (e) => {
        this.handleKeyNavigation(e, index);
      });
    });
  }

  handleKeyNavigation(event, currentIndex) {
    let newIndex;

    switch(event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        newIndex = (currentIndex + 1) % this.tabButtons.length;
        this.tabButtons[newIndex].focus();
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = (currentIndex - 1 + this.tabButtons.length) % this.tabButtons.length;
        this.tabButtons[newIndex].focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.tabButtons[currentIndex].click();
        break;
      case 'Home':
        event.preventDefault();
        this.tabButtons[0].focus();
        break;
      case 'End':
        event.preventDefault();
        this.tabButtons[this.tabButtons.length - 1].focus();
        break;
    }
  }

  // Public methods for external control
  activateTab(tabId) {
    const targetButton = this.section.querySelector(`[data-tab="${tabId}"]`);
    if (targetButton) {
      targetButton.click();
    }
  }

  getActiveTab() {
    const activeButton = this.section.querySelector('.tab-button.active');
    return activeButton?.getAttribute('data-tab') || null;
  }

  destroy() {
    // Cleanup event listeners if needed
    this.tabButtons.forEach(button => {
      button.removeEventListener('click', this.handleTabClick);
      button.removeEventListener('keydown', this.handleKeyNavigation);
    });
  }
}

// Auto-initialization
function initializePROH2OFeatures() {
  const sections = document.querySelectorAll('.pvl-proh2o-features-section');

  sections.forEach(section => {
    // Avoid double initialization
    if (!section.dataset.initialized) {
      new PRO2OProductFeatures(section);
      section.dataset.initialized = 'true';
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializePROH2OFeatures);
} else {
  initializePROH2OFeatures();
}

// Shopify theme editor support
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.pvl-proh2o-features-section');
    if (section) {
      // Reset initialization flag and create new instance
      section.dataset.initialized = 'false';
      new PRO2OProductFeatures(section);
      section.dataset.initialized = 'true';
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.pvl-proh2o-features-section');
    if (section) {
      section.dataset.initialized = 'false';
    }
  });
}

// Export for potential external use
window.PRO2OProductFeatures = PRO2OProductFeatures;